import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { ServiceAccount } from '@tinct/core-api-models';
import { useDeleteServiceAccount } from '@tinct/core-api';
import { IconAlertTriangle } from '@tabler/icons-react';
import { toast } from 'sonner';
import { Trash } from 'lucide-react';

type DeleteServiceAccountModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  serviceAccount: ServiceAccount;
};

export const DeleteServiceAccountModal = ({
  open,
  onOpenChange,
  organizationId,
  serviceAccount,
}: DeleteServiceAccountModalProps) => {
  const deleteServiceAccount = useDeleteServiceAccount({
    onSuccess: () => {
      toast.success('Service account deleted');
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete service account');
    },
  });

  const handleDelete = () => {
    if (!serviceAccount.id) return;
    deleteServiceAccount.mutate({
      organizationId,
      serviceAccountId: serviceAccount.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete service account</DialogTitle>
        </DialogHeader>

        <div className="flex gap-3 items-start rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="pt-0.5">
            <IconAlertTriangle className="size-4 text-destructive" />
          </div>
          <div className="flex flex-col gap-1 flex-1 text-destructive">
            <p className="text-sm font-medium">
              Service account: {serviceAccount.name}
            </p>
            <p className="text-sm">
              This action cannot be undone. The credential is revoked
              permanently and any integration using it will stop working.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            isLoading={deleteServiceAccount.isPending}
          >
            <Trash className="size-4" />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
